import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList } from "react-icons/fa";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomTextInput } from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import moment from "moment";

const HIGHER_CLASS_NAMES = ["XI", "XII", "11", "12"];

const AdmissionSeatAllocationConfigView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const user_id = user?.id || "";

  const viewId = location.state?.id || null;

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isHigherClass, setIsHigherClass] = useState(false);
  const [formData, setFormData] = useState({
    academicYear: "",
    className: "",
    medium: "",
    groupName: "",
    subjects: "",
    totalCapacity: "",
    disable: false,
  });

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });

  // Confirm delete modal
  const [confirmModal, setConfirmModal] = useState(false);

  useEffect(() => {
    if (viewId) {
      fetchSeatConfigData();
    }
  }, [viewId]);

  const fetchSeatConfigData = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/admission/viewSeatConfig", {
        id: Number(viewId),
      });

      if (response?.data?.status) {
        const data = response.data.data;
        const className = (data.class_name || "").trim().toUpperCase();
        const higherClass = HIGHER_CLASS_NAMES.includes(className);
        setIsHigherClass(higherClass);

        // ─── Disable logic based on screening_start_date ───
        const today = moment().format("YYYY/MM/DD");

        let isDisabled = true; // default: disabled

        if (data.screening_start_date !== null) {
          const screeningDate = moment(data.screening_start_date).format(
            "YYYY/MM/DD",
          );

          // disabled if screening date is today or in the future
          // enabled only if screening date is in the past
          isDisabled = screeningDate <= today;
        }

        setFormData({
          academicYear: data.academic_year || "",
          className: data.class_name || "",
          medium: data.medium || "",
          groupName: data.name || "",
          subjects: data.subject_name || "",
          totalCapacity:
            data.group_max_seat_count != null
              ? String(data.group_max_seat_count)
              : data.section_max_seat_count != null
                ? String(data.section_max_seat_count)
                : "",
          disable: isDisabled,
        });
      }
    } catch (error) {
      console.error("Error fetching seat config:", error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to edit
  const handleEdit = () => {
    navigate("/user/admission-seat-allocation-config/create", {
      state: { id: viewId },
    });
  };

  // Show delete confirmation
  const handleDelete = () => {
    setConfirmModal(true);
  };

  // Confirm delete - call API
  const confirmDeleteAction = async () => {
    setConfirmModal(false);
    setDeleting(true);
    try {
      const payload = {
        id: Number(viewId),
        deleted_by: Number(user_id),
      };

      const response = await AxiosInstance.post(
        "/admission/deleteSeatConfig",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            translations?.labels?.deleteSuccess ||
            "Deleted successfully!",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            translations?.labels?.deleteFailed ||
            "Failed to delete.",
        });
      }
    } catch (error) {
      console.error("Error deleting seat config:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations?.labels?.deleteFailed ||
          "Failed to delete.",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle modal ok
  const handleModalOk = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (modalState.type === "success") {
      navigate("/user/admission-seat-allocation-config");
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  // Handle back
  const handleBack = () => {
    navigate("/user/admission-seat-allocation-config");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {translations?.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Success / Error Modal */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleModalOk}
        onClose={handleModalClose}
      />

      {/* Delete Confirmation Modal */}
      <PopupModal
        open={confirmModal}
        type="warning"
        title={
          translations?.labels?.confirmDelete ||
          "Are you sure you want to delete?"
        }
        message={
          translations?.labels?.confirmDelete ||
          "Are you sure you want to delete?"
        }
        onOk={confirmDeleteAction}
        onClose={() => setConfirmModal(false)}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.seatAllocationConfiguration ||
              "Seat Allocation Configuration"}
          </h1>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="primary"
            className={`${styles.addButton} ${formData.disable ? styles.addButtonDisabled : ""}`}
            fullWidth={false}
            onClick={handleEdit}
            disabled={formData.disable}
          >
            {translations?.labels?.edit || "EDIT"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.deleteButton}
            fullWidth={false}
            onClick={handleDelete}
            loading={deleting}
            loadingText={translations?.labels?.deleting || "Deleting..."}
          >
            {translations?.labels?.delete || "DELETE"}
          </CustomButton>
        </div>

        {/* View Form (Read-only) */}
        <div className="row">
          {/* Academic Year */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations?.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={formData.academicYear}
              disabled
              readOnly
            />
          </div>

          {/* Class */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations?.labels?.classLabel || "Class"}
              name="className"
              type="text"
              value={formData.className}
              disabled
              readOnly
            />
          </div>

          {/* Medium */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations?.labels?.medium || "Medium"}
              name="medium"
              type="text"
              value={formData.medium}
              disabled
              readOnly
            />
          </div>

          {/* Group - shown only for XI / XII */}
          {isHigherClass && (
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations?.labels?.groupName || "Group"}
                name="groupName"
                type="text"
                value={formData.groupName}
                disabled
                readOnly
              />
            </div>
          )}

          {/* Subjects - shown only for XI / XII */}
          {isHigherClass && (
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations?.labels?.subjects || "Subjects"}
                name="subjects"
                type="text"
                value={formData.subjects}
                disabled
                readOnly
              />
            </div>
          )}

          {/* Total Capacity */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations?.labels?.totalCapacity || "Total Capacity"}
              name="totalCapacity"
              type="text"
              value={formData.totalCapacity}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* Back Button */}
        <div className={styles.buttonContainer}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.cancelButton}
            fullWidth={false}
            onClick={handleBack}
          >
            {translations?.labels?.back || "BACK"}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default AdmissionSeatAllocationConfigView;
